<template>
  <div class="general-settings">
    <base-form :show-back-button="false"
               @submit="submitForm">
      <el-tabs  v-model="activeName" class="top-menu-tabs">
        <el-tab-pane :label="$t('settings.general.title')" name="general">
          <div class="flex">
            <fg-input :placeholder="$t('settings.general.platform.name')"
                      :name="$t('settings.general.platform.name')"
                      :label="$t('settings.general.platform.name')"
                      :error="getError($t('settings.general.platform.name'))"
                      v-validate="modelValidations.app_name"
                      v-model="model.app_name"
                      class="w-full"
            >
            </fg-input>
          </div>
          <div class="flex">
            <fg-input class="w-full" :label="$t('settings.general.platform.language')">
              <language-select :value="$i18n.locale" @change="changeAppLang" class="language-select">
              </language-select>
            </fg-input>
          </div>
          <div class="flex">
            <fg-input :label="$t('settings.general.timezone.name')"
                      :error="getError($t('settings.general.timezone.name'))"
                      class="w-full">
              <el-select class="select-default all-country-select"
                         :name="$t('settings.general.timezone.name')"
                         :placeholder="$t('settings.general.timezone.name')"
                         filterable
                         clearable
                         v-validate="modelValidations.app_timezone"
                         v-model="model.app_timezone">
                <el-option class="select-default"
                           v-for="value in timeZoneList"
                           :key="value.value"
                           :label="value.text"
                           :value="value.utc ? value.utc[0]: value.abbr">
                </el-option>
              </el-select>
            </fg-input>
          </div>

          <div class="w-full flex">
            <fg-input :placeholder="$t('settings.general.platform.app_date_format')"
                      :name="$t('settings.general.platform.app_date_format')"
                      :label="$t('settings.general.platform.app_date_format')"
                      :error="getError($t('settings.general.platform.app_date_format'))"
                      class="w-full md:w-5/12 md:mr-6"
                      v-model="model.app_date_format">
              <template v-slot:label-right>
                <tooltip-icon-button icon="fas fa-question"
                                     class="ml-2 info-button"
                                     round
                                     :content="$t('settings.general.platform.app_date_format.tip')">
                </tooltip-icon-button>
              </template>
            </fg-input>
            <div class="w-full flex flex-col md:w-7/12 md:mr-2 mt-2">
              <span class="input-label">
                {{$t('settings.general.app_date_format.example')}}
                <a class="text-primary" href="https://date-fns.org/v2.14.0/docs/format"
                   target="_blank"
                   rel="noopener">
                  {{$t('settings.general.app_date_format.example.link')}}
                </a>
              </span>
              <span class="font-semibold mt-4">
                {{formattedDate}}
              </span>
            </div>
          </div>
          <div class="w-full flex">
            <fg-input :placeholder="$t('settings.general.platform.app_hour_format')"
                      :name="$t('settings.general.platform.app_hour_format')"
                      :label="$t('settings.general.platform.app_hour_format')"
                      :error="getError($t('settings.general.platform.app_hour_format'))"
                      class="w-full md:w-5/12 md:mr-6"
                      v-model="model.app_hour_format">
              <template v-slot:label-right>
                <tooltip-icon-button icon="fas fa-question"
                                     class="ml-2 info-button"
                                     round
                                     :content="$t('settings.general.platform.app_hour_format.tip')">
                </tooltip-icon-button>
              </template>
            </fg-input>
            <div class="w-full flex flex-col md:w-7/12 md:mr-2 mt-2">
              <span class="input-label">
                {{$t('settings.general.app_hour_format.example')}}
                <a class="text-primary" href="https://date-fns.org/v2.14.0/docs/format"
                   target="_blank"
                   rel="noopener">
                  {{$t('settings.general.app_date_format.example.link')}}
                </a>
              </span>
              <span class="font-semibold mt-4">
                {{formattedHour}}
              </span>
            </div>
          </div>
          <fg-input :placeholder="$t('settings.general.platform.use_imperial_system')"
                    :name="$t('settings.general.platform.use_imperial_system')"
                    :label="$t('settings.general.platform.use_imperial_system')"
                    :error="getError($t('settings.general.platform.use_imperial_system'))">
            <template v-slot:label-right>
              <tooltip-icon-button icon="fas fa-question"
                                   class="ml-2 info-button"
                                   round
                                   :content="$t('settings.general.platform.use_imperial_system.tip')">
              </tooltip-icon-button>
            </template>
            <base-switch v-model="model.app_use_imperial_system"/>
          </fg-input>

          <div class="flex">
            <div class="mr-4">
              <fg-input :label="$t('settings.general.platform.logo')">
                <image-upload :circle="false"
                              :src="model.app_image"
                              @on-change="handlePreview">
                </image-upload>
              </fg-input>
            </div>
            <div>
              <fg-input :label="$t('settings.general.platform.fav_icon')">
                <image-upload :circle="false"
                              :src="model.app_fav_icon"
                              @on-change="handleFavIconPreview">
                </image-upload>
              </fg-input>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane label="Email" name="email">
          <div class="flex">
            <fg-input :placeholder="$t('settings.general.platform.email_bcc')"
                      :name="$t('settings.general.platform.email_bcc')"
                      :label="$t('settings.general.platform.email_bcc')"
                      :error="getError($t('settings.general.platform.email_bcc'))"
                      v-validate="modelValidations.email"
                      v-model="model.app_email_bcc"
                      class="w-full"
            >
            </fg-input>
          </div>
          <div class="flex">
            <fg-input :placeholder="$t('settings.general.platform.email_from')"
                      :name="$t('settings.general.platform.email_from')"
                      :label="$t('settings.general.platform.email_from')"
                      :error="getError($t('settings.general.platform.email_from'))"
                      v-validate="modelValidations.email"
                      v-model="model.app_email_from"
                      class="w-full"
            >
            </fg-input>
          </div>
          <div class="flex mb-4">
            <fg-input class="w-full" :label="$t('settings.general.platform.emailSignature')">
              <wysiwyg :name="$t('settings.general.platform.emailSignature')"
                       class="form-control"
                       :class="{'has-danger': getError($t('settings.general.platform.emailSignature'))}"
                       v-model="model.app_email_signature"
              >
              </wysiwyg>
            </fg-input>
          </div>
        </el-tab-pane>

        <el-tab-pane :label="$t('settings.general.platform.terms.privacy')" name="privacy">
          <div class="flex mb-4">
            <fg-input :label="$t('settings.general.platform.terms')"
                      class="w-full"
            >
              <wysiwyg :name="$t('settings.general.platform.terms')"
                       class="form-control"
                       :class="{'has-danger': getError($t('settings.general.platform.terms'))}"
                       v-model="model.app_terms">
              </wysiwyg>
            </fg-input>
          </div>

          <div class="flex mb-4">
            <fg-input :label="$t('settings.general.platform.privacy')" class="w-full">
              <wysiwyg :name="$t('settings.general.platform.privacy')"
                       class="form-control"
                       :class="{'has-danger': getError($t('settings.general.platform.privacy'))}"
                       v-model="model.app_privacy">
              </wysiwyg>
            </fg-input>
          </div>
        </el-tab-pane>

        <el-tab-pane label="Vat" name="vat">
          <div class="flex">
            <fg-input :placeholder="$t('settings.general.platform.default_vat')"
                      :name="$t('settings.general.platform.default_vat')"
                      :label="$t('settings.general.platform.default_vat')"
                      :error="getError($t('settings.general.platform.default_vat'))"
                      v-model="model.app_vat_default"
                      class="w-full"
            >
            </fg-input>
          </div>
          <div class="flex">
            <fg-input :placeholder="$t('settings.general.platform.vat_rule')"
                      :name="$t('settings.general.platform.vat_rule')"
                      :label="$t('settings.general.platform.vat_rule')"
                      :error="getError($t('settings.general.platform.vat_rule'))"
                      v-model="model.app_vat_rule_valid_vat"
                      class="w-full">
            </fg-input>
          </div>
        </el-tab-pane>
      </el-tabs>
    </base-form>
  </div>
</template>
<script>
  import format from 'date-fns/format'
  import { mapState } from 'vuex'
  import { Upload, Select, Option } from 'element-ui'
  import ImageUpload from 'src/components/ImageUpload'
  import Tabs from 'src/components/Tabs/Tabs'
  import TabPane from 'src/components/Tabs/Tab'
  import LanguageSelect from 'src/components/LanguageSelect'
  import Wysiwyg from 'src/components/Wysiwyg/RedactorWysiwyg'
  import settingsMixin from './settingsMixin'
  import { changeLanguage } from 'src/i18n'
  import TooltipIconButton from 'src/components/Buttons/TooltipIconButton'
  import PublicService from 'src/services/PublicService'
  import SettingsService from 'src/services/SettingsService'

  export default {
    name: 'settings-general',
    mixins: [settingsMixin],
    components: {
      ImageUpload,
      TooltipIconButton,
      Wysiwyg,
      [Select.name]: Select,
      [Option.name]: Option,
      [Upload.name]: Upload,
      LanguageSelect,
      Tabs,
      TabPane
    },

    data () {
      return {
        model: {
          app_name: '',
          app_timezone: '',
          app_image: '',
          app_email_signature: '',
          app_locale: '',
          app_fav_icon: '',
          app_vat_default: '',
          app_vat_rule_valid_vat: '',
          app_email_bcc: '',
          app_email_from: '',
          app_terms: '',
          app_privacy: '',
          app_date_format: 'dd-MM-yyyy',
          app_hour_format: 'HH:mm',
          app_use_imperial_system: false,
        },
        selectedLocale: this.$i18n.locale,
        activeName: 'general'
      }
    },
    computed: {
      ...mapState({
        timeZoneList: state => state.config.timezones
      }),
      modelValidations () {
        return {
          app_name: {
            required: true,
            max: 50
          },
          app_timezone: {
            required: true
          },
          email: {
            email: true
          }
        }
      },
      formattedDate() {
        return format(new Date(), this.model.app_date_format)
      },
      formattedHour() {
        return format(new Date(), this.model.app_hour_format)
      }
    },
    methods: {
      async languageChange () {
        await PublicService.updateApiHost()
        let {data} = await SettingsService.get({limit: 200})
        this.mapSettings(data)
      },
      async changeAppLang (lang) {
        await changeLanguage(lang)
        this.model.app_locale = lang
      },
      async submitForm () {
        let res = await this.$validator.validateAll()
        if (res) {
          try {
            this.model.app_vat_default = +this.model.app_vat_default
            this.model.app_vat_rule_valid_vat = +this.model.app_vat_rule_valid_vat
            await this.updateSettings()
            this.$notify({type: 'success', message: this.$t('settings.general.success')})
          } catch (e) {
            this.$notify({type: 'danger', message: this.$t('settings.general.error')})
          }
        }
      },
      handlePreview (file) {
        this.model.app_image = file
      },
      handleFavIconPreview (file) {
        this.model.app_fav_icon = file
      }
    },
    watch: {
      '$store.state.config.textLanguage' () {
        this.languageChange()
      },
    }
  }
</script>
<style scoped>
  .top-menu-tabs {
    margin-top: -76px;
  }
  .top-menu-tabs /deep/.el-tabs__nav-wrap::after {
    @apply bg-transparent;
  }
</style>
